import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { JhiEventManager, JhiLanguageService } from '../shared/core-utils';
import { AlertService } from '../shared/alert/alert.service';
import { Principal } from '../shared';
import { NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { ParentModel } from '../shared/study/model/parent.model';
import { DesignCrossesContext } from './design-crosses.context';
import { GermplasmListService } from '../shared/germplasm-list/service/germplasm-list.service';
import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { CrossSourceEntryModel } from '../shared/study/model/cross-source-entry.model';
import { ColumnHeadersEnum } from '../shared/study/model/column-header.enum';
import { SORT_PREDICATE_NONE } from '../germplasm-manager/germplasm-search-resolve-paging-params';
import { GermplasmDetailsPopupService } from '../germplasm-details/germplasm-details-popup.service';
import { SubscriptionsManager } from '../shared/util/subscriptions-manager';
import { MAX_NAME_DISPLAY_SIZE } from '../app.constants';

@Component({
    selector: 'jhi-parent-list',
    templateUrl: './parents-list.component.html',
})
export class ParentsListComponent implements OnInit, OnDestroy {

    @Input() isFemaleParentsComponent: boolean;

    MAX_NAME_DISPLAY_SIZE = MAX_NAME_DISPLAY_SIZE;
    totalItems = 0;
    selectedItems: Map<number, ParentModel> = new Map<number, ParentModel>();
    parents: ParentModel[] = [];
    originalParentsList: ParentModel[] = [];
    lastClickIndex: any;
    isSelectAll: boolean;

    predicate: any;
    reverse: boolean;
    ColumnHeaders = ColumnHeadersEnum;
    private currentPopover: NgbPopover;

    private subscriptionsManager: SubscriptionsManager = new SubscriptionsManager();

    constructor(private jhiLanguageService: JhiLanguageService,
                public designCrossesContext: DesignCrossesContext,
                private eventManager: JhiEventManager,
                private alertService: AlertService,
                public principal: Principal,
                public translateService: TranslateService,
                private germplasmListService: GermplasmListService,
                protected germplasmDetailsPopupService: GermplasmDetailsPopupService
    ) {
    }

    ngOnDestroy(): void {
        this.subscriptionsManager.unsubscribeAll();
    }

    async ngOnInit() {
        this.subscriptionsManager.add = this.eventManager.subscribe('addToParents', (event) => {
            const lastEntryNumber = this.parents.length;
            if (event.content.isAddToFemaleParents && this.isFemaleParentsComponent) {
                this.parents.push(...event.content.parents);
            } else if (!event.content.isAddToFemaleParents && !this.isFemaleParentsComponent) {
                this.parents.push(...event.content.parents);
            }
            for (let i = lastEntryNumber; i < this.parents.length; i++) {
                this.selectedItems.set(this.parents[i].internal_id, this.parents[i]);
                this.parents[i].index = i + 1;
            }
            this.totalItems = this.parents.length;
            this.setOriginalParentsList();
            this.setContextSelected();
        });
        if (this.isFemaleParentsComponent) {
            this.subscriptionsManager.add = this.eventManager.subscribe('addToFemaleParents', (event) => {
                this.getListEntries(event.content);
            });
        } else {
            this.subscriptionsManager.add = this.eventManager.subscribe('addToMaleParents', (event) => {
                this.getListEntries(event.content);
            });
            this.subscriptionsManager.add = this.eventManager.subscribe('clearMaleParents', (event) => {
                if (this.parents.length > 0 ) {
                    this.alertService.warning('design-crosses.ignore.male.parents');
                    this.clearAll();
                }
            });
        }
        this.predicate = SORT_PREDICATE_NONE;
        this.reverse = false;
    }

    setOriginalParentsList() {
        this.originalParentsList = [...this.parents];
        this.originalParentsList.sort((a, b) => Number(a.index) > Number(b.index) ? 1 : -1);
    }

    getListEntries(lists): Promise<ParentModel[]> {
        return new Promise((resolve, reject) => {
            const index = this.totalItems + 1;
            const listIds = lists.map((list) => {
                return list.id;
            });
            this.loadFullListEntry(0, 1000, listIds, index);
            this.setOriginalParentsList();
            resolve(this.parents);
        });
    }

    private loadFullListEntry(page: number, pageSize: number, listIds: number[], index: number) {
        this.germplasmListService.getGermplasmListsData(listIds, { page, size: pageSize, sort: ['VARIABLE_8230,asc'] })
            .subscribe({
                next: (res) => {
                    res.body.forEach((entry) => {
                        this.parents.push(new ParentModel(entry.listDataId, index++, entry.data['VARIABLE_8230'], entry.data['GID'],
                            entry.data['DESIGNATION'], entry.data['CROSS'], entry.listName, this.designCrossesContext.studyName));
                    });
                    const totalItems = parseInt(res.headers.get('X-Total-Count'), 10);
                    if ((page + 1) * pageSize < totalItems) {
                        this.loadFullListEntry(page + 1, pageSize, listIds, index);
                    }
                    this.totalItems = this.parents.length;
                }
            });
    }

    selectAll(isSelectAll: boolean) {
        this.isSelectAll = isSelectAll;
        if (isSelectAll) {
            this.clearSelectedItems();
            this.onSelectPage();
        } else {
            this.clearSelectedItems();
        }
    }

    onSelectPage() {
        if (this.isPageSelected()) {
            this.selectedItems.clear();
        } else {
            // check remaining items
            this.parents.forEach((entry: ParentModel) => this.selectedItems.set(entry.internal_id, entry));
        }
        this.setContextSelected();
    }

    toggleSelect($event, index, entry: ParentModel, checkbox = false) {
        if (!$event.ctrlKey && !$event.metaKey && !checkbox) {
            this.clearSelectedItems();
        }
        let items;
        if ($event.shiftKey) {
            const max = Math.max(this.lastClickIndex, index) + 1,
                min = Math.min(this.lastClickIndex, index);
            items = this.parents.slice(min, max);
        } else {
            items = [entry];
            this.lastClickIndex = index;
        }
        const isClickedItemSelected = this.selectedItems.get(entry.internal_id);
        for (const item of items) {
            if (isClickedItemSelected) {
                this.selectedItems.delete(item.internal_id);
            } else {
                this.selectedItems.set(item.internal_id, item);
            }
        }
        this.setContextSelected();
    }

    setContextSelected() {
        if (this.isFemaleParentsComponent) {
            this.designCrossesContext.selectedFemaleParents = Array.from( this.selectedItems.values() );
        } else {
            this.designCrossesContext.selectedMaleParents = Array.from( this.selectedItems.values() );
        }
        this.isSelectAll = this.isPageSelected();
    }

    trackId(index: number, item: ParentModel) {
        return item.internal_id;
    }

    isPageSelected() {
        return this.parents.length && this.parents.every((entry: ParentModel) => Boolean(this.selectedItems.get(entry.internal_id)));
    }

    isSelected(entry: ParentModel) {
        return this.selectedItems.get(entry.internal_id);
    }

    selectEvenEntries() {
        this.clearSelectedItems();
        this.parents.forEach((entry: ParentModel) => {
            if (entry.index % 2 === 0) {
                this.selectedItems.set(entry.internal_id, entry);
            }
        });
        this.setContextSelected();
    }

    selectOddEntries() {
        this.clearSelectedItems();
        this.parents.forEach((entry: ParentModel) => {
            if (entry.index % 2 === 1) {
                this.selectedItems.set(entry.internal_id, entry);
            }
        });
        this.setContextSelected();
    }

    clearAll() {
        this.parents = [];
        this.originalParentsList = [];
        if (this.isFemaleParentsComponent) {
            this.designCrossesContext.selectedFemaleParents = [];
        } else {
            this.designCrossesContext.selectedMaleParents = [];
        }
        this.totalItems = 0;
        this.clearSelectedItems();
    }

    clearSelectedItems() {
        this.selectedItems.clear();
        this.setContextSelected();
    }

    removeSelected() {
        if (this.selectedItems.size === 0) {
            this.alertService.warning('design-crosses.selection.empty');
            return;
        }

        if (this.isPageSelected()) {
            this.parents = [];
        } else {
            this.parents = this.parents.filter((parent) => !this.selectedItems.get(parent.internal_id));
        }
        this.selectedItems.clear();

        this.parents.forEach((parent, index) => {
            parent.index = index + 1;
        });
        this.setContextSelected();
        this.totalItems = this.parents.length;
        this.setOriginalParentsList();
    }

    sort() {
        this.parents = [...this.originalParentsList];
        if (this.predicate === this.ColumnHeaders.INDEX) {
            this.parents.sort((a, b) => {
                if (this.reverse) {
                    return Number(a.index) > Number(b.index) ? 1 : -1;
                } else {
                    return Number(a.index) < Number(b.index) ? 1 : -1;
                }
            });
        } else if (this.predicate === this.ColumnHeaders.DESIGNATION) {
            this.parents.sort((a, b) => {
                if (this.reverse) {
                    return a.designation > b.designation ? 1 : -1;
                } else {
                    return a.designation < b.designation ? 1 : -1;
                }
            });
        } else if (this.predicate === this.ColumnHeaders.CROSS) {
            this.parents.sort((a, b) => {
                if (this.reverse) {
                    return a.cross > b.cross ? 1 : -1;
                } else {
                    return a.cross < b.cross ? 1 : -1;
                }
            });
        }
    }

    drop(event: CdkDragDrop<any>) {
        let index = this.parents.length;
        this.designCrossesContext.draggedItems.forEach((entry: CrossSourceEntryModel) => {
            const parent = new ParentModel(entry.entryId, ++index, entry.entryNumber, entry.gid, entry.designation,
                entry.cross, entry.sourceList, this.designCrossesContext.studyName);
            this.parents.push(parent);
            this.selectedItems.set(parent.internal_id, parent);
        });
        this.totalItems = this.parents.length;
        this.setOriginalParentsList();
        this.setContextSelected();
    }

    showRightClickMenu($event, p: NgbPopover) {
        if (this.currentPopover) {
            this.currentPopover.close();
        }
        if (this.parents && this.parents.length > 0) {
            $event.preventDefault();
            this.currentPopover = p;
            p.open();
        }
    }
}
